import {useAuth} from '@apps-common/auth-react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {Container} from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MUILink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {func, object} from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router';
import {Virtuoso} from 'react-virtuoso';
import {useDebouncedCallback} from 'use-debounce';
import {canCreatePublication, canViewDraft} from '../auth.js';
import {
  getDownloadPublicationsUrl,
  usePermissions,
  usePublications,
} from '../client.js';
import {formatDate, getNewSearchUrl, getUpdatedSearchUrl} from '../utils.js';
import DismissibleNotification from './DismissibleNotification.jsx';
import FeaturedPublications from './FeaturedPublications.jsx';
import Footer from './Footer.jsx';
import Link from './Link.jsx';
import MapToggle from './MapToggle.jsx';
import Page, {newLink} from './Page.jsx';
import PublicationsMap from './PublicationsMap.jsx';
import DataType from './filter/DataType.jsx';
import DateRangeFilter from './filter/DateRange.jsx';
import PublicationType from './filter/PublicationType.jsx';
import PublishedStatus from './filter/PublishedStatus.jsx';
import TopicFilter from './filter/Topic.jsx';
import SearchField from './form/SearchInput.jsx';

function truncate(text, length, buffer) {
  if (text.length <= length) {
    return text;
  }
  const space = text.indexOf(' ', length);
  if (space === -1 || space > length + buffer) {
    return text.substring(0, length) + '...';
  }
  return text.substring(0, space) + '...';
}

const filterParams = [
  'query',
  'geometry',
  'topic',
  'data',
  'type',
  'since',
  'before',
];

const worldPolygon = 'POLYGON ((-180 -90, 180 -90, 180 90, -180 90, -180 -90))';

function hasFilters(searchParams) {
  for (const param of filterParams) {
    if (searchParams.has(param)) {
      return true;
    }
  }
  return false;
}

function Progress() {
  const max = 5;
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const newCount = (count + 1) % max;
      setCount(newCount);
    }, 200);
    return () => clearTimeout(timeout);
  }, [count]);

  const progress = new Array(count + 1).fill('.').join('');
  return progress;
}

function PublicationItem({publication, onHover}) {
  return (
    <Container
      maxWidth="lg"
      sx={{paddingTop: 1, paddingBottom: 2}}
      onMouseEnter={() => onHover(publication.id)}
      onMouseLeave={() => onHover(null)}
    >
      <Box>
        <Typography
          variant="body2"
          sx={{marginBottom: 0.5, color: 'black.700'}}
        >
          {publication.authors[0].name} - {publication.publisher},{' '}
          {formatDate(publication.datePublished)}
        </Typography>
        <Link
          to={`/publications/${publication.id}`}
          sx={{marginBottom: 0.75, display: 'block'}}
        >
          <Typography
            variant="h6"
            component="h6"
            sx={{
              lineHeight: 1.2,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {!publication.published && (
              <Chip
                label="draft"
                size="small"
                sx={{
                  marginRight: 1,
                  marginBottom: 0.5,
                  fontVariant: 'all-small-caps',
                }}
              />
            )}
            {publication.title}
          </Typography>
        </Link>
        <Typography variant="body2">
          {truncate(publication.abstract, 250, 20)}
        </Typography>
        <Box gap={1} flexDirection="row" display="flex" flexWrap="wrap" mt={1}>
          {publication.topics.map(topic => (
            <Chip
              key={topic}
              label={topic.toUpperCase()}
              component={Link}
              to={getUpdatedSearchUrl({topic})}
              clickable
              size="small"
              sx={{
                fontSize: '11px',
              }}
            />
          ))}
        </Box>
      </Box>
    </Container>
  );
}

PublicationItem.propTypes = {
  publication: object.isRequired,
  onHover: func.isRequired,
};

function Publications() {
  const [searchParams] = useSearchParams();
  const {data: permissions} = usePermissions();
  const [hoveredId, setHoveredId] = useState(null);
  const navigate = useNavigate();
  const auth = useAuth();

  const onMapChange = useDebouncedCallback(
    geometry => {
      navigate(getUpdatedSearchUrl({geometry}));
    },
    500,
    {leading: true},
  );

  const {
    data: publications,
    count,
    error,
    more,
    loading,
  } = usePublications(searchParams.toString());

  const mapShown = !!searchParams.get('geometry');

  const onMapToggle = useCallback(
    shown => {
      if (!shown && mapShown) {
        navigate(getUpdatedSearchUrl({geometry: ''}));
      } else if (shown && !mapShown) {
        navigate(getUpdatedSearchUrl({geometry: worldPolygon}));
      }
    },
    [mapShown, navigate],
  );

  const publishedParam = searchParams.get('published');
  const onPublishedChange = useCallback(
    published => {
      navigate(getUpdatedSearchUrl({published}));
    },
    [navigate],
  );

  const topicParam = searchParams.get('topic');
  const onTopicChange = useCallback(
    topic => {
      navigate(getUpdatedSearchUrl({topic}));
    },
    [navigate],
  );

  const dataTypeParam = searchParams.get('data');
  const onDataTypeChange = useCallback(
    data => {
      navigate(getUpdatedSearchUrl({data}));
    },
    [navigate],
  );

  const typeParam = searchParams.get('type');
  const onTypeChange = useCallback(
    type => {
      navigate(getUpdatedSearchUrl({type}));
    },
    [navigate],
  );

  const sinceParam = searchParams.get('since');
  const beforeParam = searchParams.get('before');
  const onDateRangeChange = useCallback(
    params => {
      navigate(getUpdatedSearchUrl(params));
    },
    [navigate],
  );

  let status;
  if (hasFilters(searchParams)) {
    if (!loading && publications.length === 0) {
      status = <Typography>No results</Typography>;
    } else {
      let summary;
      if (loading) {
        summary = <Progress />;
      } else {
        const plural = count === 1 ? '' : 's';
        if (publications.length === count) {
          summary = `${count} result${plural}`;
        } else {
          summary = `${publications.length} of ${count} result${plural}`;
        }
      }
      status = (
        <Typography variant="caption">
          {summary}
          {!loading && auth.user && (
            <Tooltip
              title={count > 5000 ? 'download first 5000' : 'download all'}
            >
              <MUILink
                download
                target="_blank"
                href={getDownloadPublicationsUrl(
                  auth.user?.accessToken,
                  searchParams.toString(),
                )}
              >
                <FileDownloadOutlinedIcon
                  fontSize="small"
                  sx={{marginLeft: 1.5, marginBottom: -0.5}}
                />
              </MUILink>
            </Tooltip>
          )}
        </Typography>
      );
    }
  }

  const links = canCreatePublication(permissions) ? [newLink] : [];

  const Header = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: -3,
        pt: 3,
      }}
    >
      <DismissibleNotification />
      <Container maxWidth="lg">
        <SearchField />
        <Box sx={{display: 'flex', flexDirection: 'row'}}>
          <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
            <TopicFilter
              sx={{marginRight: 2, marginTop: 2}}
              value={topicParam}
              onChange={onTopicChange}
            />
            <DataType
              sx={{marginRight: 2, marginTop: 2}}
              value={dataTypeParam}
              onChange={onDataTypeChange}
            />
            <PublicationType
              sx={{marginRight: 2, marginTop: 2}}
              value={typeParam}
              onChange={onTypeChange}
            />
            <DateRangeFilter
              sx={{marginTop: 2, marginRight: 2}}
              since={sinceParam}
              before={beforeParam}
              onChange={onDateRangeChange}
            />
            <MapToggle sx={{marginTop: 2}} onChange={onMapToggle} />
            {canViewDraft(permissions) && (
              <PublishedStatus
                sx={{marginTop: 2, marginLeft: 2}}
                value={publishedParam}
                onChange={onPublishedChange}
              />
            )}
          </Box>
          {hasFilters(searchParams) && (
            <Button
              sx={{
                marginLeft: 'auto',
                marginTop: 2,
                textAlign: 'center',
              }}
              size="small"
              component={Link}
              to={getNewSearchUrl()}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Container>
      <Container maxWidth="lg">
        <Box sx={{display: 'flex', flexDirection: 'row', mt: 1, height: 24}}>
          {status}
        </Box>
      </Container>
    </Box>
  );
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <Page
      title="Planet Publications"
      links={links}
      scroll={false}
      withFooter={false}
      askAi
    >
      {error ? (
        <Alert severity="error">{error.message}</Alert>
      ) : (
        <>
          {Header}
          <Box
            sx={{
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
            }}
          >
            <Box
              sx={{
                overflow: 'hidden',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Virtuoso
                style={{
                  flexGrow: 1,
                  scrollbarGutter: isLg ? 'stable both-edges' : undefined,
                }}
                totalCount={publications.length + (more ? 1 : 0)}
                components={{
                  Header: () =>
                    !hasFilters(searchParams) ? (
                      <>
                        <FeaturedPublications />
                        <Container maxWidth="lg">
                          <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                              marginBottom: 2,
                              fontWeight: 600,
                              color: 'text.primary',
                            }}
                          >
                            Recently Published
                          </Typography>
                        </Container>
                      </>
                    ) : null,
                  Footer: () => (
                    <Container maxWidth="lg">
                      <Box>
                        <Footer />
                      </Box>
                    </Container>
                  ),
                }}
                itemContent={index => {
                  if (index < publications.length) {
                    return (
                      <PublicationItem
                        publication={publications[index]}
                        onHover={setHoveredId}
                      />
                    );
                  }
                  return (
                    <Container maxWidth="lg">
                      <Button
                        loading={loading}
                        sx={{marginTop: 2}}
                        onClick={more}
                        variant="outlined"
                      >
                        more
                      </Button>
                    </Container>
                  );
                }}
              />
            </Box>
            {mapShown && (
              <Box flexDirection="column" display="flex" flex={1}>
                <Container maxWidth="lg" sx={{flex: 1}}>
                  <Box width="100%" height="100%">
                    <PublicationsMap
                      publications={publications}
                      highlight={hoveredId}
                      onChange={onMapChange}
                    />
                  </Box>
                </Container>
              </Box>
            )}
          </Box>
        </>
      )}
    </Page>
  );
}

export default Publications;
